/**
 * Firebase Auth bootstrap (Google sign-in only).
 *
 * The web SDK config is non-secret and read from Vite env vars at build time
 * (VITE_FIREBASE_*). The server is the trust boundary: it verifies the ID token
 * and checks the teacher email allowlist, so the client never asserts a role.
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  onIdTokenChanged,
  type Auth,
  type User as FirebaseUser,
} from 'firebase/auth';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  // Analytics measurement id is optional; included for completeness but we do
  // not initialise Analytics, so its absence never blocks sign-in.
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

/** True only when the build was given real Firebase config. */
export const firebaseConfigured = Boolean(config.apiKey && config.authDomain && config.projectId);

let app: FirebaseApp | undefined;
let auth: Auth | undefined;

/** Lazily initialise Firebase so an unconfigured public-only build still runs. */
export function getFirebaseAuth(): Auth {
  if (!firebaseConfigured) {
    throw new Error('Firebase is not configured. Set VITE_FIREBASE_* env vars.');
  }
  if (!app) app = initializeApp(config);
  if (!auth) auth = getAuth(app);
  return auth;
}

const provider = new GoogleAuthProvider();
provider.setCustomParameters({ prompt: 'select_account' });

export async function signInWithGoogle(): Promise<FirebaseUser> {
  const result = await signInWithPopup(getFirebaseAuth(), provider);
  return result.user;
}

export async function signOut(): Promise<void> {
  await fbSignOut(getFirebaseAuth());
}

/** Subscribe to auth + token changes. Returns an unsubscribe function. */
export function watchAuth(cb: (user: FirebaseUser | null) => void): () => void {
  return onIdTokenChanged(getFirebaseAuth(), cb);
}

/** Current Firebase ID token, force-refreshed if requested. */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  if (!firebaseConfigured) return null;
  const user = getFirebaseAuth().currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

export type { FirebaseUser };
